import {Injectable} from '@angular/core';
import { CplUserStatus } from '../../supervision-management/compliance-items/compliance-item/enums/cpl-user-status.enum';
import { CplItem } from '../../model/cpl-item.model';
import { AccessRoleHelper } from '../../model/helpers/access-role.helper';
import { CplCycle } from '../../model/cpl-cycle.model';
import { BaseItemHelpersService } from '../base-item-helpers/base-item-helpers.service';
import { DateHelpers, SessionService, User } from '@edl-development/edl-common';
import { UserSummary } from 'src/app/model/user-summary.model';
import { VerificationStatus } from 'src/app/supervision-management/compliance-items/compliance-item/enums/verification-status.enum';
import { CplItemSubType } from 'src/app/model/enum/cpl-item-sub-type.enum';

const PENDING_SUBMISSION_STATUSES = [CplUserStatus.DEFAULT, CplUserStatus.EXPIRING, CplUserStatus.EXPIRED];
const COMPLETED_STATUSES = [CplUserStatus.DECLINED, CplUserStatus.DELETED, CplUserStatus.FORGIVEN, CplUserStatus.VERIFIED];

@Injectable()
export class ComplianceItemHelpersService {
    constructor(
        private baseItemHelpersService: BaseItemHelpersService,
        private sessionService: SessionService
    ) {}

    isFinalUserStatus(cycleStatus: CplUserStatus) {
        return COMPLETED_STATUSES.find((completedCycleStatus: CplUserStatus) => completedCycleStatus === cycleStatus) !== undefined;
    }

    isPendingSubmission(cycleStatus: CplUserStatus) {
        return PENDING_SUBMISSION_STATUSES.find((completedCycleStatus: CplUserStatus) => completedCycleStatus === cycleStatus) !== undefined;
    }

    isPendingApproval(cycleStatus: CplUserStatus) {
        return cycleStatus === CplUserStatus.PENDING;
    }

    isEvidenceRequirementComplete(userCycle: CplCycle, complianceItem: CplItem) {
        return !complianceItem.EvidenceRequired || userCycle.HasAttachments;
    }

    isQuestionnaireRequirementComplete(userCycle: CplCycle, complianceItem: CplItem) {
        return !complianceItem.QfmRequired || userCycle.HasCompletedQuestionnaire
    }

    areAllSubmissionRequirmenetsComplete(userCycle: CplCycle, complianceItem: CplItem) {
        return this.isEvidenceRequirementComplete(userCycle, complianceItem) && 
            this.isQuestionnaireRequirementComplete(userCycle, complianceItem)
    }

    isItemReadyToSubmit(userCycle: CplCycle, complianceItem: CplItem) {
        return complianceItem.IsPublished &&
            this.areAllSubmissionRequirmenetsComplete(userCycle, complianceItem) &&
            this.isPendingSubmission(userCycle.UserStatus);
    }

    isAct48Enabled = (complianceItem: CplItem) => {
        const portalSettings = this.sessionService.PortalSettings;
        return portalSettings.Act48Enabled && complianceItem != null && complianceItem.Act48Detail != null && complianceItem.Act48Detail.Applicable;
    }

    canEditItem(complianceItem: CplItem, user: User) {
        if (complianceItem.IsDeleted) {
            return false;
        }
        
        if (!complianceItem.IsTemplate) {
            return complianceItem.CreatedBy == user.UserID || AccessRoleHelper.isAdmin(user.AccessRoleID);
        }
        else
        {
            return AccessRoleHelper.isEDLAdmin(user.AccessRoleID);
        }
    }

    getAllApprovers(complianceItem: CplItem) {
        const stages = complianceItem.VerificationStages ?? [];
        return this.baseItemHelpersService.getAllApprovers(stages);
    }

    userStatusToString(userStatus: CplUserStatus) {
        switch(userStatus) {
            case CplUserStatus.VERIFIED:
                return 'Approved';
            case CplUserStatus.EXPIRING:
                return 'Expiring';
            case CplUserStatus.PENDING:
                return 'Pending';
            case CplUserStatus.EXPIRED:
                return 'Expired';
            case CplUserStatus.DELETED:
                return 'Deleted';
            case CplUserStatus.DECLINED:
                return 'Disapproved';
            case CplUserStatus.FORGIVEN:
                return 'Forgiven';
            case CplUserStatus.COMPLETE_NOTALL_APPROVED:
                return 'Complted Not All Approved';
            case CplUserStatus.DEFAULT:
            default:
                return 'Waiting on User';
        }
    }

    GetDaysRemaining(summaryItem: CplCycle | UserSummary){
        const endDate = summaryItem.EndDate && DateHelpers.shortDateStringToDate(summaryItem.EndDate);
        if (DateHelpers.isMinDate(endDate)) {
            return null;
        }

        const diff = DateHelpers.GetDaysBetween(new Date(), endDate);
        if (diff < 0 && (
            summaryItem.UserStatus == CplUserStatus.VERIFIED ||
            summaryItem.UserStatus == CplUserStatus.PENDING || 
            summaryItem.UserStatus == CplUserStatus.DECLINED ||
            summaryItem.UserStatus == CplUserStatus.FORGIVEN)) {
                return 0;
        }
        return diff ;
    }

    Negative(summaryItem: CplCycle | UserSummary) {
        const diff = this.GetDaysRemaining(summaryItem);
        if (diff !== null && diff < 0 ) {
            return true;
        }
        else {
            return false;
        }
    }

    isItemActive = (complianceItem: CplItem) => {
        return complianceItem.IsPublished && !complianceItem.IsDeleted && !complianceItem.IsArchived;
    }

    isVerifier = (complianceItem: CplItem) => {
        const currentUser = this.sessionService.user;
        for (var i = 0; i < complianceItem.VerificationStages.length; i++) { 
            if (complianceItem.VerificationStages[i].Approvers.filter( user =>  user.UserID == currentUser.UserID).length > 0){
                return true;
            }
        }

        return false;
    }

    canBulkVerifyAllCycles = (cplCycles: CplCycle[]) => {
        if (cplCycles.length === 0) {
            return false;
        }

        return cplCycles.every((userCycle: CplCycle) => userCycle.CanVerify);
    }

    canManageItem(complianceItem: CplItem) {
        const currentUser = this.sessionService.user;
        const isVerifier = this.isVerifier(complianceItem);
        return complianceItem.AssignedUsers.filter( user => user.Type != 0 && user.UserID == currentUser.UserID).length > 0
                            || isVerifier
                            || complianceItem.CreatedBy == currentUser.UserID
                            || AccessRoleHelper.isAdmin(currentUser.AccessRoleID);
    }

    canNudge = (cplCycle: CplCycle) => {
       return cplCycle.UserStatus === CplUserStatus.DEFAULT || cplCycle.UserStatus === CplUserStatus.EXPIRING || cplCycle.UserStatus === CplUserStatus.EXPIRED || cplCycle.UserStatus === CplUserStatus.COMPLETE_NOTALL_APPROVED; 
    }

    canModifyDate = (cplCycle: CplCycle, complianceItem: CplItem, isHistory: boolean) => {
        const CanManageItem = this.canManageItem(complianceItem);
        return !isHistory && CanManageItem && cplCycle.UserStatus != CplUserStatus.VERIFIED && cplCycle.UserStatus != CplUserStatus.DECLINED && cplCycle.UserStatus != CplUserStatus.FORGIVEN;
    }

    canRemoveFromItem = (complianceItem: CplItem, isHistory: boolean) => {
        const CanManageItem = this.canManageItem(complianceItem);
        return !isHistory && CanManageItem
    }

    canRemoveHours = ({
        CanManageItem,
        cplCycle,
        complianceItem
    } : {
        CanManageItem: boolean,
        cplCycle: CplCycle, 
        complianceItem: CplItem
    }) => {
        return this.isAct48Enabled(complianceItem) && this.canPerformVerificationAction({ CanManageItem, cplCycle, complianceItem, action: VerificationStatus.REOPEN });
    }

    canPerformBulkVerificationAction = ({
        CanManageItem,
        IsVerifier,
        complianceItem,
        action
    } : {
        CanManageItem: boolean,
        IsVerifier: boolean,
        complianceItem: CplItem, 
        action: VerificationStatus
    }) => {
        if (!this.canPerformVerificationAction_internal(complianceItem, CanManageItem)) {
            return false;
        }

        switch(action) {
            case VerificationStatus.APPROVED:
                return IsVerifier;
            case VerificationStatus.DECLINED:
                return complianceItem.SubType !== CplItemSubType.CLEARANCE;
            case VerificationStatus.FORGIVEN:
                return complianceItem.SubType !== CplItemSubType.CLEARANCE;
            case VerificationStatus.REOPEN:
                return true;
            case VerificationStatus.SUBMIT:
            default:
                return false;
        }
    }
    
    canPerformVerificationAction = ({
        CanManageItem,
        cplCycle,
        complianceItem,
        action
    } : {
        CanManageItem: boolean,
        cplCycle: CplCycle, 
        complianceItem: CplItem, 
        action: VerificationStatus 
    }) => {
        if (!this.canPerformVerificationAction_internal(complianceItem, CanManageItem)) {
            return false;
        }

        const isFinalUserStatus = this.isFinalUserStatus(cplCycle.UserStatus);

        switch(action) {
            case VerificationStatus.APPROVED:
                return cplCycle.CanVerify;
            case VerificationStatus.DECLINED:
                return !isFinalUserStatus && complianceItem.SubType !== CplItemSubType.CLEARANCE;
            case VerificationStatus.FORGIVEN:
                return !isFinalUserStatus && complianceItem.SubType !== CplItemSubType.CLEARANCE;
            case VerificationStatus.REOPEN:
                return cplCycle.UserStatus === CplUserStatus.VERIFIED || cplCycle.UserStatus === CplUserStatus.DECLINED || cplCycle.UserStatus === CplUserStatus.FORGIVEN || cplCycle.UserStatus === CplUserStatus.COMPLETE_NOTALL_APPROVED || cplCycle.UserStatus === CplUserStatus.PENDING;
            case VerificationStatus.SUBMIT:
                return this.isItemReadyToSubmit(cplCycle, complianceItem);
            default:
                return false;
        }
    }

    private canPerformVerificationAction_internal(complianceItem: CplItem, CanManageItem: boolean) {
        return this.isItemActive(complianceItem) && CanManageItem;
    }
}